import React from 'react';
import { View } from 'react-native';
import TouchableElement from '@components/common/touchableElement';
import { font as initialFont } from '@styles/fonts';
import { scale as initialScale } from '@components/frame';
import AnimatedRow from './AnimatedRow';
import { SCREEN_BORDER_RADIUS } from '@v2/pages/common/constants/base.constants';
import { AnimatedTableProps } from '@components/settings/components/common/animatedTable';
import { injectConditionalObject } from '@helpers';

export interface TableContentProps<T = any>
  extends Pick<
      AnimatedTableProps<T>,
      | 'C'
      | 'array'
      | 'onSubRowPress'
      | 'config'
      | 'scale'
      | 'font'
      | 'selectedItem'
      | 'cellRenderer'
      | 'renderAccordionContent'
      | 'getAccordionItemsCount'
      | 'styleForLastItem'
      | 'hideBorderForLastItem'
    >,
    Pick<Required<AnimatedTableProps>, 'onPress' | 'valuesAlreadyTranslated' | 'lineSize' | 'showAllButton'>,
    Pick<AnimatedTableProps<T>, 'onPressIn'> {
  containerWidth: number;
}

const TableContent = <T = any,>({
  C,
  array,
  onPress,
  onPressIn,
  onSubRowPress,
  showAllButton,
  valuesAlreadyTranslated,
  lineSize,
  config,
  scale = initialScale,
  font = initialFont,
  containerWidth,
  selectedItem,
  cellRenderer,
  renderAccordionContent,
  getAccordionItemsCount,
  styleForLastItem,
  hideBorderForLastItem,
}: TableContentProps<T>) => {
  return (
    <>
      {array.map((item, index) => {
        return (
          <TouchableElement
            onPress={() => onPress(item)}
            onPressIn={onPressIn ? () => onPressIn(item) : undefined}
            key={`arrayForRender-${(item as any).name || (item as any).sequenceNumber || index}-${index}`}
            hitSlop={{ top: 0, bottom: 0, left: 0, right: 0 }}
          >
            {({ boxStyle, pressHoverColor }) => {
              const isLastElement = index === array.length - 1;
              return (
                <View
                  style={[
                    { backgroundColor: C(pressHoverColor('Background/Light/Field')), width: '100%' },
                    isLastElement && { borderBottomLeftRadius: scale(SCREEN_BORDER_RADIUS), borderBottomRightRadius: scale(SCREEN_BORDER_RADIUS) },
                  ]}
                >
                  <AnimatedRow
                    item={item}
                    index={index}
                    boxStyle={boxStyle}
                    pressHoverColor={pressHoverColor}
                    config={config}
                    C={C}
                    valuesAlreadyTranslated={valuesAlreadyTranslated}
                    font={font}
                    scale={scale}
                    lineSize={lineSize}
                    showAllButton={showAllButton}
                    array={array}
                    containerWidth={containerWidth}
                    selectedItem={selectedItem}
                    cellRenderer={cellRenderer}
                    onSubRowPress={onSubRowPress}
                    renderAccordionContent={renderAccordionContent}
                    getAccordionItemsCount={getAccordionItemsCount}
                    {...injectConditionalObject(isLastElement, { styleForLastItem, hideBorderForLastItem })}
                  />
                </View>
              );
            }}
          </TouchableElement>
        );
      })}
    </>
  );
};

// Memoize TableContent to prevent re-renders when parent updates but props haven't changed
export default React.memo(TableContent, (prevProps, nextProps) => {
  // Only re-render if array or key props changed
  return (
    prevProps.array === nextProps.array &&
    prevProps.selectedItem === nextProps.selectedItem &&
    prevProps.config === nextProps.config &&
    prevProps.containerWidth === nextProps.containerWidth
  );
}) as typeof TableContent;
